<template>
    <div class="modal-card card">
        <form @submit.prevent>
            <div class="card-header">
                <!-- Title -->
                <h4 class="card-header-title">Acknowledge or deny warnings</h4>
                <!-- Close -->
                <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>

            <div class="card-body">
                <dl class="pairs">
                    <div>
                        <dt>Certificate</dt>
                        <dd>{{ modalData.commonName }}</dd>
                    </div>
                    <div>
                        <dt>Warnings</dt>
                        <dd>{{ warningList.length }} raised before deployment</dd>
                    </div>
                </dl>

                <p class="step-say">These checks ran against the pending change. Acknowledging accepts them and lets
                    deployment continue; denying stops the change so you can correct it first.</p>

                <div class="warn-list" v-if="warningList.length">
                    <div class="warn-item" v-for="(warning, index) in warningList" :key="index">
                        <i class="fa fa-triangle-exclamation warn-ico"></i>
                        <div>
                            <div class="warn-title">{{ warning.title }}</div>
                            <div class="warn-detail" v-if="warning.detail">{{ warning.detail }}</div>
                        </div>
                    </div>
                </div>
                <p class="step-say mb-0" v-else>No warnings were reported for this change.</p>

                <div class="alert alert-danger mb-0 mt-3" role="alert" v-if="error">{{ error }}</div>
            </div>

            <div class="card-footer d-flex align-items-center gap-3">
                <div class="ms-auto d-flex gap-2">
                    <button type="button" class="btn btn-secondary" data-bs-dismiss="modal"
                        :disabled="updateInProgress">Cancel</button>
                    <button type="button" class="btn btn-danger" :disabled="updateInProgress"
                        @click="onFormSubmit('deny')">
                        <div v-if="pending === 'deny'" class="spinner-border spinner-border-sm" role="status">
                            <span class="visually-hidden">Loading...</span>
                        </div>
                        <div v-else>
                            Deny
                        </div>
                    </button>
                    <button type="button" class="btn btn-success" :disabled="updateInProgress"
                        @click="onFormSubmit('acknowledge')">
                        <div v-if="pending === 'acknowledge'" class="spinner-border spinner-border-sm" role="status">
                            <span class="visually-hidden">Loading...</span>
                        </div>
                        <div v-else>
                            Acknowledge
                        </div>
                    </button>
                </div>
            </div>
        </form>
    </div>
</template>

<script>
import { mapActions } from "vuex";
export default {
    data() {
        return {
            error: "",
            updateInProgress: false,
            pending: "",
        };
    },
    props: {
        modalData: {
            type: [Object, Array, String],
        },
    },
    name: "AcknowledgeDenyWarnings",
    computed: {
        warningList() {
            const raw = this.modalData?.warnings;
            if (!raw) return [];
            if (Array.isArray(raw)) return raw.map(item => this.toWarning(item));
            if (typeof raw === "string") {
                return raw.split(/\r?\n/).map(line => line.trim()).filter(Boolean).map(line => this.toWarning(line));
            }
            return Object.entries(raw).map(([key, value]) => ({
                title: this.humanize(key),
                detail: typeof value === "string" ? value : JSON.stringify(value),
            }));
        },
    },
    methods: {
        ...mapActions("aut", {
            ackDenyWarnings: "acknowledgeDenyWarnings",
        }),
        humanize(value) {
            return String(value).replace(/[-_]+/g, " ").replace(/^./, c => c.toUpperCase());
        },
        toWarning(line) {
            const [title, ...rest] = String(line).split(":");
            return { title: title.trim(), detail: rest.join(":").trim() };
        },
        async onFormSubmit(ackDeny) {
            this.updateInProgress = true;
            this.pending = ackDeny;
            this.error = "";
            let toastOptions = {
                message: "Warnings Acknowledged/Denied successfully.",
                type: "success"
            }
            await this.ackDenyWarnings({
                commonName: this.modalData.commonName,
                payload: {
                    acknowledgement: ackDeny
                }
            }).then(() => {
                this.$store.state.aut.getCertStatusTrigger = !this.$store.state.aut.getCertStatusTrigger
            }).catch((error) => {
                if (error.response.data.error)
                    this.error = error.response.data.error;
                else this.error = error.message;
                toastOptions = {
                    message: error || "Something went wrong while acknowledging/denying warnings. Please contact support.",
                    type: "error"
                }
                console.error("Error in acknowledge: ", this.error);
                return;
            }).finally(() => {
                this.updateInProgress = false;
                this.pending = "";
                this.$store.dispatch("addToast", toastOptions);
            })
            this.$store.dispatch("hideModal");
        },
    },
};
</script>

<style lang="scss" scoped>
@use '../../theme/scss/user-variables' as *;

.pairs {
    margin: 0 0 1rem;
}

.pairs>div {
    display: grid;
    grid-template-columns: 6rem 1fr;
    gap: 1rem;
    padding: .4rem 0;
    align-items: baseline;
}

.pairs>div+div {
    border-top: 1px solid #EDF2F9;

    body.dark-mode & {
        border-top-color: $dark-border-color;
    }
}

.pairs dt {
    font-weight: 400;
    color: #6E84A3;
    font-size: .8125rem;
    margin: 0;

    body.dark-mode & {
        color: $dark-text-secondary;
    }
}

.pairs dd {
    margin: 0;
    font-size: .8125rem;
    word-break: break-all;
}

.step-say {
    font-size: .8125rem;
    color: #6E84A3;
    margin: 0 0 1.25rem;

    body.dark-mode & {
        color: $dark-text-secondary;
    }
}

/* Warnings --------------------------------------------------------------- */
.warn-list {
    border: 1px solid #F7E2A8;
    border-radius: .5rem;
    background: #FFFCF3;
    padding: .25rem 1rem;

    body.dark-mode & {
        border-color: rgba(246, 195, 67, .35);
        background: rgba(246, 195, 67, .08);
    }
}

.warn-item {
    display: flex;
    gap: .75rem;
    padding: .75rem 0;
    border-top: 1px solid rgba(246, 195, 67, .35);
}

.warn-item:first-child {
    border-top: none;
}

.warn-ico {
    color: #F6C343;
    margin-top: .15rem;
}

.warn-title {
    font-size: .875rem;
    font-weight: 500;
}

.warn-detail {
    font-size: .8125rem;
    color: #6E84A3;
    margin-top: .15rem;
    word-break: break-word;

    body.dark-mode & {
        color: $dark-text-secondary;
    }
}
</style>
